// components/CustomEntityModal.tsx
import React, { useState } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import { createCustomEntity } from "../services/customentity";
import { EntityType, entityTypes } from "../utils/types";

interface CustomEntityModalProps {
  show: boolean;
  onHide: () => void;
  onEntityCreated: () => void;
}

const CustomEntityModal: React.FC<CustomEntityModalProps> = ({
  show,
  onHide,
  onEntityCreated,
}) => {
  const [entityTag, setEntityTag] = useState("");
  const [selectedEntityType, setSelectedEntityType] = useState<string>("");
  // Use transparent colors by default
  const [color, setColor] = useState("rgba(230, 243, 255, 0.5)"); // Default light blue with transparency
  const [borderColor, setBorderColor] = useState("rgba(0, 123, 255, 0.7)"); // Default border color with transparency
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!entityTag.trim()) {
      setError("Entity name is required");
      return;
    }

    if (!selectedEntityType) {
      setError("Please select a base entity type");
      return;
    }

    // Convert to uppercase and replace spaces with underscores for consistency
    const formattedName = entityTag.trim().toUpperCase().replace(/\s+/g, "_");

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await createCustomEntity({
        entity_type: selectedEntityType, // Use the selected entity type
        entity_tag: formattedName, // Use the formatted custom entity name
        metadata: {
          color: color,
          border_color: borderColor,
        },
      });

      if (result) {
        onEntityCreated();
        handleClose();
      } else {
        setError("Failed to create custom entity");
      }
    } catch (err) {
      setError("An error occurred while creating the entity");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setEntityTag("");
    setSelectedEntityType("");
    // Reset to transparent colors when closing
    setColor("rgba(230, 243, 255, 0.5)");
    setBorderColor("rgba(0, 123, 255, 0.7)");
    setError(null);
    onHide();
  };

  // Helper function to convert hex to rgba with transparency
  const hexToRgba = (hex: string, alpha: number = 0.5) => {
    // Remove the # if it exists
    hex = hex.replace("#", "");

    // Parse the hex values to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Return the rgba value
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Handle color change from color picker (which returns hex) to rgba
  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const hexColor = e.target.value;
    setColor(hexToRgba(hexColor, 0.5)); // 0.5 opacity
  };

  const handleBorderColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const hexColor = e.target.value;
    setBorderColor(hexToRgba(hexColor, 0.7)); // 0.7 opacity for border
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Create Custom Entity</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Entity Type</Form.Label>
            <Form.Select
              value={selectedEntityType}
              onChange={(e) => setSelectedEntityType(e.target.value)}
              disabled={isSubmitting}
            >
              <option value="">Select an entity type</option>
              {entityTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Select the base entity type for your custom entity
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Custom Entity Name</Form.Label>
            <Form.Control
              type="text"
              value={entityTag}
              onChange={(e) => setEntityTag(e.target.value)}
              placeholder="e.g. PROTEIN_COMPLEX"
              disabled={isSubmitting}
            />
            <Form.Text className="text-muted">
              Name will be converted to uppercase with underscores
            </Form.Text>
          </Form.Group>

          <div className="row mb-3">
            <div className="col">
              <Form.Group>
                <Form.Label>Highlight Color</Form.Label>
                <div className="d-flex align-items-center">
                  <Form.Control
                    type="color"
                    // Use a suitable hex equivalent for the color picker
                    value={color.startsWith("rgba") ? "#e6f3ff" : color}
                    onChange={handleColorChange}
                    disabled={isSubmitting}
                    className="me-2"
                  />
                  <div
                    style={{
                      width: "100%",
                      height: "30px",
                      backgroundColor: color,
                      border: `2px solid ${borderColor}`,
                      borderRadius: "4px",
                    }}
                  ></div>
                </div>
                <Form.Text className="text-muted">
                  Colors will be applied with transparency for better text
                  visibility
                </Form.Text>
              </Form.Group>
            </div>

            <div className="col">
              <Form.Group>
                <Form.Label>Border Color</Form.Label>
                <div className="d-flex align-items-center">
                  <Form.Control
                    type="color"
                    // Use a suitable hex equivalent for the color picker
                    value={
                      borderColor.startsWith("rgba") ? "#007bff" : borderColor
                    }
                    onChange={handleBorderColorChange}
                    disabled={isSubmitting}
                    className="me-2"
                  />
                  <div
                    style={{
                      width: "100%",
                      height: "30px",
                      backgroundColor: borderColor,
                      borderRadius: "4px",
                    }}
                  ></div>
                </div>
              </Form.Group>
            </div>
          </div>

          <div className="mb-3">
            <Form.Label>Preview:</Form.Label>
            <div
              className="border rounded p-2 text-center"
              style={{
                backgroundColor: color,
                border: `2px solid ${borderColor}`,
                padding: "10px",
                fontSize: "14px",
              }}
            >
              {entityTag
                ? entityTag.toUpperCase().replace(/\s+/g, "_")
                : "CUSTOM_ENTITY_PREVIEW"}
            </div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Creating..." : "Create Entity"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CustomEntityModal;
